import React, { useEffect, useMemo, useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Card, FormGroup } from "@cofano/cck";
import { Tabs, Tab, Box, CircularProgress, Alert, Tooltip } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { Widgets } from "@mui/icons-material";

import CommentIndicatorButton from "components/comments/CommentIndicatorButton";

import { DataFormat, Fields, hasOptionalFieldPermission, hasPluginPermission, usePermission } from "imports/Utils";

import { CargoLink, DossierLink, LocationLink, ProductLink, RelationLink } from "utils/LinkUtils";
import { calculateDifference } from "utils/cargoActionUtils";

import { CallFromCallSideviewer, CargoActionFromCallSideviewer, CargoFromCallSideviewer } from "view/calls/components/sideviewer/useCallSideviewer";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import UpdateCargoActionFieldsDialog from "view/cargos/components/dialogs/UpdateCargoActionFieldsDialog";

import { GetCargoForCallSideviewerDocument } from "__generated__/view/cargos/graphql/cargo.queries.generated";
import { CallType, CommentEntityType, StatusType } from "__generated__/enums";

type CargoTab = {
	actionId: number;
	cargoId: number;
	type: CallType;
	label: string;
};

const CallSideviewerCargoBlock = ({
	call,
	openComment,
	onActionChange,
	onLoadingChange,
	forceReadOnly,
}: {
	call: CallFromCallSideviewer
	openComment: (cargoId: number, type: CommentEntityType.Cargo) => void;
	onActionChange: (action: CargoActionFromCallSideviewer | null, cargo: CargoFromCallSideviewer | null) => void;
	onLoadingChange: (loading: boolean) => void;
	forceReadOnly?: boolean;
}) => {

	const session = useStoreState(store => store.session);

	const { isRTR, isReadOnly } = usePermission();

	const hasCargoNumber = hasOptionalFieldPermission(session, Fields.ORDER, "cargoNumber");

	const tabs = useMemo(() => {
		const actions = [ ...(call.cargos ?? []) ].sort((a, b) => a.id - b.id);

		return actions.map((action): CargoTab => ({
			actionId: action.id,
			cargoId: action.cargo?.id ?? 0,
			type: action.type,
			label: `${intl.formatMessage({ id: action.type })} ${(hasCargoNumber && action.cargo?.cargoNumber) || action.cargo?.product?.name}`,
		}));
	}, [ call.cargos, hasCargoNumber ]);

	const [ selectedIndex, setSelectedIndex ] = useState(0);
	const selectedTab = tabs[selectedIndex] ?? tabs[0];

	const [ dialogOpen, openDialog ] = useState(false);

	const { data, loading, refetch } = useQuery(GetCargoForCallSideviewerDocument, {
		variables: {
			id: selectedTab?.cargoId ?? 0,
		},
		skip: !selectedTab,
	});

	const cargo = data?.cargo;
	const currentAction = cargo?.actions?.find(a => a.id === selectedTab?.actionId) ?? null;
	const documents = cargo?.documents ?? [];

	useEffect(() => {
		if (loading) {
			return;
		}
		onActionChange(currentAction, cargo ?? null);
	}, [ currentAction, cargo, loading ]);

	useEffect(() => {
		onLoadingChange(loading);
	}, [ loading ]);

	const difference = currentAction ? calculateDifference(currentAction) : "";

	if (tabs.length === 0) {
		return (
			<Alert severity="info">
				{intl.formatMessage({ id: "NO_CARGOS_FOUND" })}
			</Alert>
		);
	}

	return (
		<div className="call-sideviewer-cargo">
			<Card
				title={cargo?.product?.name}
				subtitle={currentAction && intl.formatMessage({ id: currentAction.type })}
				icon={<Widgets fontSize="small" color="primary" />}
				collapsable
				actions={cargo ? [ {
					icon: <EditIcon fontSize="small" />,
					onClick: () => openDialog(true),
					hidden: forceReadOnly || !(isRTR && !isReadOnly),
				}, {
					render: () => (
						<CommentIndicatorButton
							entity={cargo}
							session={session}
							onClick={() => openComment(cargo.id, CommentEntityType.Cargo)}
							color="primary"
						/>
					),
				} ] : undefined}
				disablePadding>
				{tabs.length > 1 && (
					<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
						<Tabs
							value={selectedIndex}
							onChange={(_, newValue) => setSelectedIndex(newValue)}
							variant="scrollable"
							scrollButtons="auto">
							{tabs.map((tab, index) => (
								<Tab label={tab.label} value={index} key={`${tab.cargoId}-${tab.type}`} />
							))}
						</Tabs>
					</Box>
				)}

				<Box padding={1.5}>
					{loading ? (
						<CircularProgress size={24} />
					) : !cargo ? (
						<div>{intl.formatMessage({ id: "NO_CARGOS_FOUND" })}</div>
					) : (
						<React.Fragment>
							<FormGroup label={intl.formatMessage({ id: "ORDER_CURRENT_STATUS" })} labelPlacement="left-center">
								<StatusChanger
									session={session}
									entity={cargo}
									statusType={StatusType.Cargo}
									refetch={() => refetch()}
									showHistory />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "PRODUCT" })} labelPlacement="left-center">
								<ProductLink product={cargo.product} />
							</FormGroup>

							<FormGroup label={intl.formatMessage({ id: "LOCATION" })} labelPlacement="left-center">
								<LocationLink location={currentAction?.location}  />
							</FormGroup>

							{hasCargoNumber && (
								<FormGroup label={intl.formatMessage({ id: "CARGO_NUMBER" })} labelPlacement="left-center">
									{cargo.cargoNumber}
								</FormGroup>
							)}

							{selectedTab?.type === CallType.Loading && (
								<FormGroup label={intl.formatMessage({ id: "LOADING_REFERENCE" })} labelPlacement="left-center">
									{cargo.loadingReference}
								</FormGroup>
							)}

							{selectedTab?.type === CallType.Discharging && (
								<FormGroup label={intl.formatMessage({ id: "DISCHARGING_REFERENCE" })} labelPlacement="left-center">
									{cargo.dischargingReference}
								</FormGroup>
							)}

							{cargo.charter ? (
								<FormGroup label={intl.formatMessage({ id: "CHARTER_ORDER" })} labelPlacement="left-center">
									<Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
										{isRTR ? <DossierLink charter={cargo.charter} /> : cargo.charter?.charterNumber}

										<Tooltip title={documents.length > 0 ? `${documents.length} ${intl.formatMessage({ id: "ORDER_DOCUMENTS" })}` : intl.formatMessage({ id: "NO_DOCUMENTS" })}>
											<span>
												<DescriptionOutlinedIcon
													fontSize="small"
													color={documents.length > 0 ? "success" : "inherit"} />
											</span>
										</Tooltip>
									</Box>
								</FormGroup>
							) : (
								<FormGroup label={`${intl.formatMessage({ id: "CARGO" })} / ${intl.formatMessage({ id: "ORDER" })}`} labelPlacement="left-center">
									<Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
										<CargoLink cargo={cargo} />

										<Tooltip title={documents.length > 0 ? `${documents.length} ${intl.formatMessage({ id: "ORDER_DOCUMENTS" })}` : intl.formatMessage({ id: "NO_DOCUMENTS" })}>
											<span>
												<DescriptionOutlinedIcon
													fontSize="small"
													color={documents.length > 0 ? "success" : "inherit"} />
											</span>
										</Tooltip>
									</Box>
								</FormGroup>
							)}

							{hasOptionalFieldPermission(session, Fields.ORDER, "supplier") && (
								<FormGroup label={intl.formatMessage({ id: "SUPPLIER" })} labelPlacement="left-center">
									<RelationLink relation={cargo.supplier} />
								</FormGroup>
							)}

							{hasOptionalFieldPermission(session, Fields.ORDER, "weight") && (
								<FormGroup label={intl.formatMessage({ id: "ORDER_WEIGHT" })} labelPlacement="left-center">
									{DataFormat.tonnage(currentAction?.weight)}
								</FormGroup>
							)}

							{hasOptionalFieldPermission(session, Fields.ORDER, "volume") && (
								<FormGroup label={intl.formatMessage({ id: "ORDER_VOLUME" })} labelPlacement="left-center">
									{DataFormat.volumeM3(currentAction?.volume)}
								</FormGroup>
							)}

							{hasPluginPermission(session, "voyageLogs") && (
								<React.Fragment>
									{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") && (
										<FormGroup label={intl.formatMessage({ id: "VOYAGELOG_LOCATION_WEIGHT_VOLUME" })} labelPlacement="left-center">
											{DataFormat.volumeWithUnit(currentAction?.measurements?.locationVolume ?? 0, currentAction?.measurements.locationVolumeUnit)}
											{DataFormat.mtonWeightWithUnit(currentAction?.measurements?.locationWeight ?? 0, currentAction?.measurements.locationWeightUnit)}
										</FormGroup>
									)}

									{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "shipMeasurement") && (
										<FormGroup label={intl.formatMessage({ id: "VOYAGELOG_SKIPPER_WEIGHT_VOLUME" })} labelPlacement="left-center">
											{DataFormat.volumeWithUnit(currentAction?.measurements?.skipperVolume ?? 0, currentAction?.measurements.skipperVolumeUnit)}
											{DataFormat.mtonWeightWithUnit(currentAction?.measurements?.skipperWeight ?? 0, currentAction?.measurements.skipperWeightUnit)}
										</FormGroup>
									)}

									{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "protest") && (
										<React.Fragment>
											<FormGroup label={intl.formatMessage({ id: "PERCENTUAL_DIFFERENCE" })} labelPlacement="left-center">
												{difference}
											</FormGroup>

											<FormGroup label={intl.formatMessage({ id: "VOYAGELOG_PROTEST" })} labelPlacement="left-center">
												{DataFormat.boolean(currentAction?.measurements?.protest)}
											</FormGroup>
										</React.Fragment>
									)}

									{hasOptionalFieldPermission(session, Fields.CARGO_ACTION, "landMeasurement") && (
										<React.Fragment>
											{currentAction?.type === CallType.Loading && (
												<React.Fragment>
													<FormGroup label={intl.formatMessage({ id: "RELATIVE_DENSITY_AT_LOADING" })} labelPlacement="left-center">
														{DataFormat.simpleUnit(currentAction?.measurements?.relativeDensity, DataFormat.UNITS.DENSITY)}
													</FormGroup>

													<FormGroup label={intl.formatMessage({ id: "PRODUCT_LOADING_TEMPERATURE" })} labelPlacement="left-center">
														{DataFormat.simpleUnit(currentAction?.measurements?.productTemperature, DataFormat.UNITS.CELSIUS)}
													</FormGroup>
												</React.Fragment>
											)}

											<FormGroup label={intl.formatMessage({ id: "CARGO_CONSIGNOR" })} labelPlacement="left-center">
												{currentAction?.measurements?.actualConsignor}
											</FormGroup>

											<FormGroup label={intl.formatMessage({ id: "ORDER_CONSIGNEE" })} labelPlacement="left-center">
												{currentAction?.measurements?.actualConsignee}
											</FormGroup>
										</React.Fragment>
									)}
								</React.Fragment>
							)}
						</React.Fragment>
					)}
				</Box>
			</Card>

			{cargo && (
				<UpdateCargoActionFieldsDialog
					open={dialogOpen}
					cargo={cargo}
					voyageId={call.voyage.id}
					callId={call.id}
					onClose={() => openDialog(false)}
					onSave={() => refetch()} />
			)}
		</div>
	);
};

export default CallSideviewerCargoBlock;
